import {
  Firestore,
  Database,
  NotificationsCollection,
  NotificationsDoc,
  NotificationsCreate,
  NotificationsRetrieve,
  NotificationsOpen,
  NotificationsSynchronize,
  NotificationsItem,
  NotificationsAck,
  NotificationsAnnexe,
  NotificationsExtra,
  NotificationsUploadFiles,
  NotificationsUploadExtraFiles,
  HistoryCollection,
  HistoryDoc,
  UsersDoc,
  GroupDoc,
  ExportDmsNotifications,
  GetDocFromReference,
} from '@/firebase-exports'
import { runTransaction } from 'firebase/firestore'
import store from '@/store/index'
import i18n from '@/i18n'
import getErrorText from '@/utils/get-error-text'
import {
  getOperatorFilters,
  getUserOperatorsDict,
  withHeavyCache,
} from './operator-service'
import { openNotificationDemo } from './notification-demo-service'
import { updatePlan } from './plan-service'
import { normalizeSearch } from '@/utils/string-utils'
import { getGroupOperators } from '@/services/groups-service'
import { isGroupRef } from '@/services/user-service'

// caches privadas del servicio
const operatorFiltersCache = new Map()
const heavyOperatorsMap = new Map()
const operatorActiveCifsCache = new Map()
const cifToOperatorsCache = new Map()

/**
 * Crea una nueva notificación con los datos proporcionados y la marca como abierta.
 *
 * @param {Object} notificationData - Los datos de la notificación a crear.
 * @returns {Promise<string>} Una promesa que se resuelve con el ID de la notificación creada.
 */
export async function notificationCreate(notificationData) {
  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  const notificationId = (
    await NotificationsCreate({ userId, ...notificationData })
  ).data

  await NotificationsOpen({ id: notificationId })

  return notificationId
}

/**
 * Recupera las notificaciones actuales.
 *
 * @returns {Promise<Array>} Una promesa que se resuelve con un arreglo de notificaciones.
 */
export async function notificationsRetrieve() {
  return await NotificationsRetrieve()
}

/**
 * Actualiza las notificaciones del usuario. Si se fuerza la actualización o ha pasado más de una hora desde la última actualización, se recupera la última información de notificaciones.
 *
 * @param {boolean} [forceUpdate=false] - Indica si se debe forzar la actualización de notificaciones.
 */
export async function updateNotifications(forceUpdate = false) {
  // -------------------------------------------------------------------------------------------------------
  // NO actualizar notificaciones desde el DMS, ya estan insertadas.
  // No elimino esta funcion + la siguiente + varios import , por si acaso mas adelante nos arrepentimos.
  // -------------------------------------------------------------------------------------------------------
  // GOODOK-2080 Restaurar sincronizacion (de manera temporal),
  // ya lo dejo preparado para reactivarlo facilmente en futuras ocasiones o incluso hacerlo configurable ...
  ///return
  const autoUpdate = true
  if (autoUpdate) {
    if (
      store.state.user.notificacionesActivas &&
      !store.state.user.needsCertEmpresa &&
      (forceUpdate ||
        moreThanOneHour(store.state.user.lastRetrievedNotifications?.toDate()))
    ) {
      store.commit('setProgress', {
        message: 'updatingNotificationsText',
      })

      withTimeout(notificationsRetrieve(), 60000) // Llamada con timeout de 60 segundos
        .then((result) => {
          store.commit('setProgress', {
            message: 'updatingNotificationsText',
            isDisplayed: false,
          })

          if (result.data.errors) {
            const title = i18n.t('errorRetrieve')
            const message = i18n.t('errorRetrieveMessage')
            const error = i18n.t('genericError')
            //const error = i18n.t('errorRetreiveCifs') + result.data.errors.join(', ')

            store.commit('setDialog', {
              title,
              message,
              error,
              maxWidth: '550',
            })
          }
        })
        .catch((err) => {
          store.commit('setProgress', {
            message: 'updatingNotificationsText',
            isDisplayed: false,
          })
          if (err.toString().includes('Timeout after')) {
            return
          }

          // El deadline-exceeded a veces puede ser por una mala conexión a Internet, evitamos mostrar error en este caso concreto
          // En caso de que haya fallado, en el siguiente login o refresco reintentará; si fue todo Ok, estarán todas las notificaciones
          if (!err.toString().includes('deadline-exceeded')) {
            const title = i18n.t('errorRetrieve')
            const message = i18n.t('errorRetrieveMessage')
            const error = getErrorText(err.toString())
            store.commit('setDialog', {
              title,
              message,
              error,
              maxWidth: '550',
            })
          }
        })

      updatePlan(true)
    }
  }
}

// Funcion sin uso al haber anulado la obtencion de nuevas notificaciones.
function withTimeout(promise, ms) {
  const timeout = new Promise((_, reject) =>
    setTimeout(() => reject(new Error(`Timeout after ${ms}ms`)), ms)
  )

  return Promise.race([promise, timeout]) // La que termine primero
}

/**
 * Concatena para posteriormente poder buscar indistintamente en el atributo concepto y
 * en el atributo conceptoXML del array de los metadatas, si es que existe.
 */
function joinConcepts(doc) {
  const conceptoXML = doc.metadatas?.find((m) => m.conceptoXML)?.conceptoXML
  return conceptoXML ? `${doc.concepto} - ${conceptoXML}` : doc.concepto
}

/**
 * Actualiza una notificación específica con los datos proporcionados.
 *
 * @param {string} notificationId - ID de la notificación a actualizar.
 * @param {Object} notification - Los datos de actualización para la notificación.
 * @returns {Promise<any>} Una promesa que se resuelve con la respuesta de la actualización.
 */
export async function updateNotification(notificationId, notification) {
  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  var response = await Firestore.updateDoc(
    NotificationsDoc(userId, notificationId),
    notification
  )
  return response
}

/**
 * Actualiza la observación de una notificación en Firestore.
 *
 * @param {string} notificationId - ID de la notificación a actualizar.
 * @param {string} observation - Nueva observación a asignar a la notificación.
 * @returns {Promise<any>} - Promesa que se resuelve con la respuesta de la actualización.
 */
export async function updateObservation(notificationId, observation) {
  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  const batch = Firestore.writeBatch(Database)

  batch.update(NotificationsDoc(userId, notificationId), {
    observation: observation || Firestore.deleteField(),
  })

  batch.set(
    HistoryDoc(
      userId,
      'notifications',
      notificationId,
      Firestore.doc(HistoryCollection(userId, 'notifications', notificationId))
        .id
    ),
    {
      actionType: observation
        ? 'notificationObservation'
        : 'notificationObservationDeleted',
      observation: observation || Firestore.deleteField(),
      createdAt: Firestore.serverTimestamp(),
      // Contemplar modo sustitucion
      ...(store.getters.isSubstituteMode
        ? {
            userRef: UsersDoc(store.state.userSubstitute.id),
            absentUserRef: UsersDoc(store.state.user.id),
          }
        : {
            userRef: UsersDoc(store.state.user.id),
          }),
    },
    { merge: true }
  )

  return await batch.commit()
}

/**
 * Actualiza la respuesta de un operador en una notificación asignada a un grupo.
 *
 * @param {string} notificationId - ID de la notificación a actualizar.
 * @param {boolean} response - respuesta del operador si acepta o no la responsabilidad.
 * @returns {Promise<any>} - Promesa que se resuelve con la respuesta de la actualización.
 */
export async function saveOperatorResponse(notificationId, response) {
  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  const ref = NotificationsDoc(userId, notificationId)
  const userRef = UsersDoc(store.state.user.id)

  await runTransaction(Database, async (transaction) => {
    const snap = await transaction.get(ref)
    const data = snap.data()

    // Anulado el control de sobreescritura:
    // Se decide que un operador pueda 'arrepentirse' de su decisión anterior (p.e. Rechazar)
    // y ahora decida o lo obliguen a Aceptar, con lo que podemos llegar a tener 2 entradas
    // del mismo operador en el array de respuestas de los operadores, una rechazada y otra
    // posterior en el tiempo aceptada.
    /*
    const alreadyResponded = data.responseOperators?.some(
      (r) => r.userRef.id === userRef.id
    )
    if (alreadyResponded) return // ya respondio anteriormente, no reescribir
    */

    // Lo reconvierto en una proteccion de sobreescritura solo si la respuesta es igual a
    // la respuesta anterior, por si acaso se cuela.
    const alreadyResponded = data.responseOperators?.some(
      (r) => r.userRef.id === userRef.id && r.response === response
    )
    if (alreadyResponded) return // ya respondio anteriormente lo mismo, no reescribir

    //const createdAt = Firestore.serverTimestamp() // no se puede usar: serverTimestamp + arrayUnion, da error en firebase.
    const createdAt = Date.now() // timestamp local, ms
    const newResponse = {
      userRef,
      response,
      createdAt,
    }

    const updateData = { responseOperators: Firestore.arrayUnion(newResponse) }
    if (response === true) updateData.managerAccepted = true
    transaction.update(ref, updateData)

    // Añadir entrada en el history con la respuesta del operador
    transaction.set(
      HistoryDoc(
        userId,
        'notifications',
        notificationId,
        Firestore.doc(
          HistoryCollection(userId, 'notifications', notificationId)
        ).id
      ),
      {
        actionType: response === false ? 'managerDecline' : 'managerAccept',
        createdAt: Firestore.serverTimestamp(),
        // Contemplar modo sustitucion
        ...(store.getters.isSubstituteMode
          ? {
              userRef: UsersDoc(store.state.userSubstitute.id),
              absentUserRef: UsersDoc(store.state.user.id),
            }
          : {
              userRef: UsersDoc(store.state.user.id),
            }),
      },
      { merge: true }
    )
  })
}

/**
 * Acepta una notificación por parte del manager.
 *
 * @param {string} notificationId - ID de la notificación a aceptar.
 * @returns {Promise<any>} Una promesa que se resuelve con la respuesta del proceso de aceptación.
 */
export async function managerAcceptNotification(notificationId) {
  const batch = Firestore.writeBatch(Database)

  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  batch.update(NotificationsDoc(userId, notificationId), {
    managerAccepted: true,
  })

  batch.set(
    HistoryDoc(
      userId,
      'notifications',
      notificationId,
      Firestore.doc(HistoryCollection(userId, 'notifications', notificationId))
        .id
    ),
    {
      actionType: 'managerAccept',
      createdAt: Firestore.serverTimestamp(),
      // Contemplar modo sustitucion
      ...(store.getters.isSubstituteMode
        ? {
            userRef: UsersDoc(store.state.userSubstitute.id),
            absentUserRef: UsersDoc(store.state.user.id),
          }
        : {
            userRef: UsersDoc(store.state.user.id),
          }),
    },
    { merge: true }
  )

  return await batch.commit()
}

/**
 * Rechaza una notificación por parte del manager.
 *
 * @param {string} notificationId - ID de la notificación a rechazar.
 * @returns {Promise<any>} Una promesa que se resuelve con la respuesta del proceso de rechazo.
 */
export async function managerDeclineNotification(notificationId) {
  const batch = Firestore.writeBatch(Database)

  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  batch.update(NotificationsDoc(userId, notificationId), {
    managerRef: Firestore.deleteField(),
    managerAccepted: false,
  })

  batch.set(
    HistoryDoc(
      userId,
      'notifications',
      notificationId,
      Firestore.doc(HistoryCollection(userId, 'notifications', notificationId))
        .id
    ),
    {
      actionType: 'managerDecline',
      createdAt: Firestore.serverTimestamp(),
      // Contemplar modo sustitucion
      ...(store.getters.isSubstituteMode
        ? {
            userRef: UsersDoc(store.state.userSubstitute.id),
            absentUserRef: UsersDoc(store.state.user.id),
          }
        : {
            userRef: UsersDoc(store.state.user.id),
          }),
    },
    { merge: true }
  )

  return await batch.commit()
}

/**
 * Asigna o desasigna un manager a una notificación.
 *
 * @param {string} notificationId - ID de la notificación.
 * @param {string} manager - ID del manager a asignar. Si es null, desasigna el manager actual.
 * @param {boolean} [isAdmin=false] - Indica si el manager asignado es un administrador.
 * @returns {Promise<any>} Una promesa que se resuelve con la respuesta del proceso de asignación.
 */
export async function assignManager(
  notificationId,
  manager,
  isAdmin = false,
  managerType
) {
  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  const batch = Firestore.writeBatch(Database)

  batch.update(
    NotificationsDoc(userId, notificationId),
    manager
      ? managerType == 'group'
        ? {
            managerRef: GroupDoc(userId, manager),
            managerAccepted: false,
            responseOperators: Firestore.deleteField(),
          }
        : {
            managerRef: UsersDoc(manager),
            managerAccepted: isAdmin,
            responseOperators: Firestore.deleteField(),
          }
      : {
          managerRef: Firestore.deleteField(),
          managerAccepted: Firestore.deleteField(),
          responseOperators: Firestore.deleteField(),
        }
  )

  batch.set(
    HistoryDoc(
      userId,
      'notifications',
      notificationId,
      Firestore.doc(HistoryCollection(userId, 'notifications', notificationId))
        .id
    ),
    {
      actionType: manager ? 'assignManager' : 'unassignManager',
      createdAt: Firestore.serverTimestamp(),
      // Contemplar modo sustitucion
      ...(store.getters.isSubstituteMode
        ? {
            userRef: UsersDoc(store.state.userSubstitute.id),
            absentUserRef: UsersDoc(store.state.user.id),
          }
        : {
            userRef: UsersDoc(store.state.user.id),
          }),
      //targetUserRef: manager ? UsersDoc(manager) : Firestore.deleteField(),
      // Posible asignacion de un grupo como responsable
      targetUserRef: manager
        ? managerType == 'group'
          ? GroupDoc(userId, manager)
          : UsersDoc(manager)
        : Firestore.deleteField(),
    },
    { merge: true }
  )

  return await batch.commit()
}

/**
 * Recupera una notificación específica.
 *
 * @param {string} userId - ID del usuario.
 * @param {string} notificationId - ID de la notificación a recuperar.
 * @returns {Promise<Object>} Una promesa que se resuelve con los datos de la notificación.
 */
export async function getNotification(userId, notificationId) {
  var notification = undefined
  const docSnap = await Firestore.getDoc(
    NotificationsDoc(userId, notificationId)
  )
  const document = docSnap.data()
  if (document) {
    if (document.managerRef)
      document.manager = await GetDocFromReference(document.managerRef)

    if (document.fecha_puesta_disposicion)
      document.fecha_puesta_disposicion =
        document.fecha_puesta_disposicion.toDate()

    if (document.fecha_aceptacion)
      document.fecha_aceptacion = document.fecha_aceptacion.toDate()

    if (document.fecha_expiracion)
      document.fecha_expiracion = document.fecha_expiracion.toDate()

    if (document.alert) document.alert.date = document.alert.date.toDate()

    document.estadoOriginal = document.estado

    notification = { id: notificationId, ...document }
  }
  return notification
}

/**
 * Crea una subscripción a una notificación específica, actualizando el contexto con los cambios en tiempo real.
 *
 * @param {string} userId - ID del usuario.
 * @param {string} notificationId - ID de la notificación a la que suscribirse.
 * @param {Object} context - Contexto del componente donde se realiza la subscripción.
 * @returns {Function} Función para cancelar la subscripción.
 */
export async function getNotificationSubscription(
  userId,
  notificationId,
  context
) {
  try {
    const docSnap = await Firestore.getDoc(
      NotificationsDoc(userId, notificationId)
    )
    if (!docSnap.exists()) return
  } catch (error) {
    return
  }

  // Snapshot to check the notification
  var notificationUnsubscribe = Firestore.onSnapshot(
    NotificationsDoc(userId, notificationId),
    async (doc) => {
      const document = doc.data()
      // Setting the record in the context
      if (document) {
        if (document.managerRef)
          document.manager = await GetDocFromReference(document.managerRef)

        if (document.fecha_puesta_disposicion)
          document.fecha_puesta_disposicion =
            document.fecha_puesta_disposicion.toDate()

        if (document.fecha_aceptacion)
          document.fecha_aceptacion = document.fecha_aceptacion.toDate()

        if (document.fecha_expiracion)
          document.fecha_expiracion = document.fecha_expiracion.toDate()

        if (document.alert) document.alert.date = document.alert.date.toDate()

        document.estadoOriginal = document.estado

        context.notification = { id: notificationId, ...document }

        if (context.loadingNotification) context.loadingNotification = false
      }
    },
    (error) => {
      throw error
    }
  )

  return notificationUnsubscribe
}

/**
 * Filtra las notificaciones según los roles y permisos del usuario.
 *
 * @param {Array<Object>} notificationsParam - Las notificaciones a filtrar.
 * @param {Object} user - El usuario para el cual se realiza el filtrado.
 * @returns {Array<Object>} Un array de notificaciones filtradas.
 */
export function filterNotifications(notificationsParam, user) {
  let notifications = notificationsParam
  let operatorRoles = user.operatorRoles ?? []
  let operatorGroups = user.groupsRef ?? []
  const isSuperAdmin = !user.parentRef

  // Si es administrador o si es "Ver Todo", no filtramos nada
  if (!isSuperAdmin && operatorRoles[0]?.name !== 'noFilter') {
    notifications = notificationsParam.filter((n) => {
      // En caso de que sea el responsable de la notificación, autorizarla
      if (n.managerRef && n.managerRef.id === user.id) return true

      // Segun las especificaciones de la tarea GOODOK-2039 si el responsable es un grupo y el operador
      // pertenece a ese grupo debe tomar en cuenta solo las empresas, y no aplicar tambien los filtros.
      if (n.managerRef && isGroupRef(n.managerRef)) {
        if (n.managerRef.id === operatorGroups[0]?.id) return true
      }

      return operatorRoles.some((role) => {
        const filter = [
          ...role.administrationLevelAllowed,
          ...role.autonomicAllowed,
          ...role.sourceEntityAllowed,
        ]
        if (!role.isCustom) {
          // Filtros genéricos
          if (
            filter.includes(n.administrationLevel) ||
            filter.includes(n.autonomicCode) ||
            filter.includes(n.codigo_organismo_emisor) ||
            filter.includes(n.codigo_organismo_emisor_raiz)
          )
            return true
        } else {
          // Filtros específicos del usuario
          if (filter.length > 0) {
            const isInFilter =
              filter.includes(n.administrationLevel) ||
              filter.includes(n.autonomicCode) ||
              filter.includes(n.codigo_organismo_emisor) ||
              filter.includes(n.codigo_organismo_emisor_raiz)
            if (isInFilter && role.negativeEntities) return false
            else if (!isInFilter && !role.negativeEntities) return false
          }

          // --- Validación de oficina remitente ---
          let passesOficinaRemitente = true
          if (
            Array.isArray(role.oficinaRemitente) &&
            role.oficinaRemitente.length > 0
          ) {
            if (!role.comparisonOficina || role.comparisonOficina === '==') {
              passesOficinaRemitente = role.oficinaRemitente.some((oficina) =>
                n.metadatas?.some((m) =>
                  m.oficinaRemitente
                    ?.toLowerCase()
                    .includes(oficina.toLowerCase())
                )
              )
            } else if (role.comparisonOficina === '!=') {
              // Ninguna oficina del documento puede estar en el filtro
              passesOficinaRemitente =
                n.metadatas?.every(
                  (m) =>
                    !role.oficinaRemitente.some((oficina) =>
                      m.oficinaRemitente
                        ?.toLowerCase()
                        .includes(oficina.toLowerCase())
                    )
                ) ?? true
            }

            if (!passesOficinaRemitente) return false
          }

          // --- Validación de concepto ---
          let passesConcepto = true
          if (Array.isArray(role.concepto) && role.concepto.length > 0) {
            const joinedConcepts = normalizeSearch(
              joinConcepts(n).toLowerCase()
            )
            if (!role.comparisonConcepto || role.comparisonConcepto === '==') {
              // Al menos un concepto configurado debe estar contenido en el string concatenado del doc
              passesConcepto = role.concepto.some((c) =>
                joinedConcepts.includes(normalizeSearch(c.toLowerCase()))
              )
            } else if (role.comparisonConcepto === '!=') {
              // Ningún concepto configurado puede estar contenido en el string concatenado del doc
              passesConcepto = role.concepto.every(
                (c) =>
                  !joinedConcepts.includes(normalizeSearch(c.toLowerCase()))
              )
            }

            if (!passesConcepto) return false
          }

          // --- Validación de tipos ---

          // If there is a missing field we do not check the types and we accept the notification
          //if (!role.types || !role.comparisonType) return true

          let passesTypes = true
          if (
            Array.isArray(role.types) &&
            role.types.length > 0 &&
            role.comparisonType
          ) {
            //return role.comparisonType == '=='
            passesTypes =
              role.comparisonType === '=='
                ? role.types.some(
                    // Comparing the type with the ones in the metadatas.
                    // We just need one type in the metadata to be in the filter type to accept the notification.
                    // If the notification does not have metadatas, we return false
                    (type) =>
                      n.metadatas?.some((m) => m.tipo === type) ||
                      n.conceptoTipo === type
                  )
                : role.types.every(
                    // Comparing the type with the ones in the metadatas.
                    // All the types in the notification have to be different than the ones in the filter.
                    // If the notification does not have metadatas, we return true
                    // (all the types are different because there are no types)
                    (type) =>
                      (n.metadatas?.every((m) => m.tipo !== type) ?? true) &&
                      n.conceptoTipo !== type
                  )
          }

          return passesOficinaRemitente && passesConcepto && passesTypes
        }
      })
    })
  }

  return notifications
}

/**
 * Lista todas las notificaciones para un usuario específico, aplicando filtros y transformaciones necesarias.
 *
 * @param {string} userId - El ID del usuario.
 * @param {Object} user - El objeto de usuario con detalles adicionales para el filtrado.
 * @param {Object} [period] - El objeto que contiene el periodo para recuperar las notificaciones.
 * @returns {Promise<Array<Object>>} Una promesa que se resuelve con un array de notificaciones.
 */
export async function listAllNotifications(userId, user, period = undefined) {
  const operatorsObject = await getUserOperatorsDict(userId, true)
  const { unit, amount, initialDate, finalDate } = period || {}
  let querySnap

  // caso 'Todo', mantener la retro-compatibilidad con lo que había.
  if (!period || (unit == 'days' && amount == 0)) {
    querySnap = await Firestore.getDocs(
      Firestore.query(
        NotificationsCollection(userId),
        Firestore.orderBy('fecha_puesta_disposicion', 'desc')
      )
    )
  } else {
    if (unit) {
      const hasDateRange = !!(initialDate && finalDate)

      const todaysDate = new Date()
      let from, to
      from = new Date(todaysDate)
      switch (unit) {
        case 'days':
          from.setDate(todaysDate.getDate() - amount)
          to = new Date().setDate(todaysDate.getDate())
          break
        case 'months':
          from.setMonth(todaysDate.getMonth() - amount)
          to = new Date().setDate(todaysDate.getDate())
          break
        case 'years':
          from.setFullYear(todaysDate.getFullYear() - amount)
          to = new Date().setDate(todaysDate.getDate())
          break
        case 'dateRange':
          if (hasDateRange) {
            from = new Date(initialDate)
            to = new Date(finalDate)
          }
          break
      }

      from.setHours(0, 0, 0, 0)
      const fromDate = new Date(from)
      const toDate = new Date(to)
      toDate.setHours(23, 59, 59, 999)

      querySnap = await Firestore.getDocs(
        Firestore.query(
          NotificationsCollection(userId),
          Firestore.orderBy('fecha_puesta_disposicion', 'desc'),
          Firestore.startAt(toDate),
          Firestore.endAt(fromDate)
        )
      )
    }
  }

  let notifications = await Promise.all(
    //FSC
    querySnap.docs.map(async (doc) => {
      /*const historyCollection = Firestore.collection(doc.ref, 'history')
      const historySnapshot = await Firestore.getDocs(historyCollection)
      const history = historySnapshot.docs.map((historyDoc) => ({
        id: historyDoc.id,
        ...historyDoc.data(),
      }))*/
      const document = doc.data()
      if (document.managerRef)
        document.manager =
          operatorsObject[document.managerRef.id] ??
          (await GetDocFromReference(document.managerRef))

      if (document.fecha_puesta_disposicion)
        document.fecha_puesta_disposicion =
          document.fecha_puesta_disposicion.toDate()

      if (document.fecha_aceptacion)
        document.fecha_aceptacion = document.fecha_aceptacion.toDate()

      if (document.fecha_expiracion)
        document.fecha_expiracion = document.fecha_expiracion.toDate()

      if (document.alert) document.alert.date = document.alert.date.toDate()

      document.estadoOriginal = document.estado

      return { ref: doc.ref, id: doc.id, ...document /*, history */ }
    })
  )

  // Filtramos por CIF, si es operador los Ids no coinciden
  if (userId !== user.id) {
    const cifs = user.cifs.map((c) => c.numeroDocIdentidad)
    notifications = notifications.filter((notification) => {
      // los superadmin y los operadores con permisos de admin deben poder ver las notificaciones sin nif titular.
      if (
        (store.getters.isSuperAdminUser || store.getters.isSuperOperatorUser) &&
        !notification.nif_titular
      )
        return true

      return (
        cifs.includes(notification.nif_titular) &&
        user.cifsPermissions[notification.nif_titular]?.notificacionesActivas
      )
    })
  }

  // Filtramos por filters
  return filterNotifications(notifications, user)
}

/**
 * Crea una subscripción a las notificaciones del usuario, actualizando los datos en tiempo real.
 *
 * @param {string} userId - El ID del usuario.
 * @param {Object} user - El objeto de usuario para aplicar filtros de notificación.
 * @param {number} [days=90] - Número de días hacia atrás desde hoy para incluir en la subscripción.
 * @param {number} [limit=0] - Número máximo de notificaciones a devolver tras ejecutar los filtros.
 * @returns {Function} Una función para cancelar la subscripción.
 */
export async function notificationsSubscription(
  userId,
  user,
  days = 90,
  limit = 0
) {
  const todaysDate = new Date()

  // Establecer la hora, minutos, segundos y milisegundos a 0 porque
  // se quedan fuera notificaciones del dia calculado pero de antes de la hora actual y
  // asi obtenemos todas las del dia calculado a partir de la medianoche.
  const from = new Date()
  from.setDate(todaysDate.getDate() - days)
  from.setHours(0, 0, 0, 0)
  const fromDate = new Date(from)

  const to = new Date().setDate(todaysDate.getDate() + 1) // Add + 1 day to get new docs after initializing subscription
  const toDate = new Date(to)

  const operatorsObject = await getUserOperatorsDict(userId, true)

  const unsub = Firestore.onSnapshot(
    days != 0
      ? // Taking the last X days
        Firestore.query(
          NotificationsCollection(userId),
          Firestore.orderBy('fecha_puesta_disposicion', 'desc'),
          Firestore.startAt(toDate),
          Firestore.endAt(fromDate)
        )
      : // Taking all
        Firestore.query(
          NotificationsCollection(userId),
          Firestore.orderBy('fecha_puesta_disposicion', 'desc')
        ),
    async (querySnapshot) => {
      let notifications = await Promise.all(
        querySnapshot.docs.map(async (doc) => {
          /*const historyCollection = Firestore.collection(doc.ref, 'history')
          const historySnapshot = await Firestore.getDocs(historyCollection)
          const history = historySnapshot.docs.map((historyDoc) => ({
            id: historyDoc.id,
            ...historyDoc.data(),
          }))*/
          const document = doc.data()
          if (document.managerRef) {
            document.manager =
              operatorsObject[document.managerRef.id] ??
              (await GetDocFromReference(document.managerRef))
          }

          if (document.fecha_puesta_disposicion)
            document.fecha_puesta_disposicion =
              document.fecha_puesta_disposicion.toDate()

          if (document.fecha_aceptacion)
            document.fecha_aceptacion = document.fecha_aceptacion.toDate()

          if (document.fecha_expiracion)
            document.fecha_expiracion = document.fecha_expiracion.toDate()

          if (document.alert) document.alert.date = document.alert.date.toDate()

          document.estadoOriginal = document.estado

          return { ref: doc.ref, id: doc.id, ...document /*, history*/ }
        })
      )

      // Filtramos por CIF, si es operador los Ids no coinciden
      if (userId !== user.id) {
        const cifs = user.cifs.map((c) => c.numeroDocIdentidad)
        notifications = notifications.filter((notification) => {
          // los superadmin y los operadores con permisos de admin deben poder ver las notificaciones sin nif titular.
          if (
            (store.getters.isSuperAdminUser ||
              store.getters.isSuperOperatorUser) &&
            !notification.nif_titular
          )
            return true

          return (
            cifs.includes(notification.nif_titular) &&
            user.cifsPermissions[notification.nif_titular]
              ?.notificacionesActivas
          )
        })
      }

      // Filtramos por filters
      const finalNotifications = filterNotifications(notifications, user)

      // Si limit es mayor que cero, devolvemos los limit primeros
      const limitedNotifications =
        limit > 0 ? finalNotifications.slice(0, limit) : finalNotifications

      // Guardamos en el store
      store.commit('setNotifications', {
        notifications: limitedNotifications,
        reloaded: true,
      })
    }
  )

  store.commit('setNotificationsUnsubscribe', unsub)
  return unsub
}

/**
 * Acepta una notificación específica.
 *
 * @param {Object} notification - La notificación a aceptar.
 * @param {Object} [absentUser=null] - El usuario ausente que acepta la notificación en nombre del usuario principal.
 * @param {boolean} [synchronized=false] - Indica si la aceptación debe sincronizarse.
 * @returns {Promise<any>} Una promesa que se resuelve con la respuesta del proceso de aceptación.
 */
export async function acceptNotification(
  notification,
  absentUser = null,
  synchronized = false
) {
  const payload = {
    id: notification.id,
    ...(absentUser && absentUser.id && { absentUserId: absentUser.id }),
    synchronized,
  }
  return store.getters.isDemoEnv
    ? await openNotificationDemo({ id: notification.id })
    : (await NotificationsOpen(payload)).data
}

/**
 * Sincroniza los identificadores del documento y de los items de una notificación específica.
 *
 * @param {Object} notification - La notificación a sincronizar.
 * @returns {Promise<any>} Una promesa que se resuelve con la respuesta del proceso de sincronización.
 */
export async function synchronizeNotification(notification) {
  return (await NotificationsSynchronize({ id: notification.id })).data
}

/**
 * Abre un elemento específico de una notificación.
 *
 * @param {Object} notification - La notificación a abrir.
 * @param {string} item - El elemento de la notificación a abrir.
 * @param {boolean} shared - Indica si la notificación es compartida.
 * @returns {Promise<any>} Una promesa que se resuelve con la respuesta del proceso de apertura.
 */
export async function openNotification(notification, item, shared) {
  // Devolver no solo el contenido sino un objeto con el contenido y el tipo del contenido, asi:
  // { data: 'JVBERi ...' ,
  //   contentType: 'application/pdf' o 'text/richtext' o 'application/xml' o lo que sea que contenga.
  // }
  return (
    await NotificationsItem({
      id: notification.id,
      item,
      shared,
    })
  ).data
}

/**
 * Abre el acuse de recibo de una notificación.
 *
 * @param {Object} notification - La notificación a la que se accede.
 * @param {boolean} shared - Indica si la notificación es compartida.
 * @returns {Promise<any>} Una promesa que se resuelve con la respuesta del proceso de apertura.
 */
export async function openAck(notification, shared) {
  return (await NotificationsAck({ id: notification.id, shared })).data
}

/**
 * Abre un anexo de una notificación específica.
 *
 * @param {Object} notification - La notificación a la que se accede.
 * @param {Object} annexe - El anexo a abrir.
 * @param {boolean} shared - Indica si la notificación es compartida.
 * @returns {Promise<any>} Una promesa que se resuelve con la respuesta del proceso de apertura.
 */
export async function openAnnexe(notification, annexe, shared) {
  try {
    return (
      await NotificationsAnnexe({
        id: notification.id,
        annexe,
        shared,
      })
    ).data
  } catch (error) {
    //console.log('Anexo no disponible', annexe.nombre)
  }
}

/**
 * Abre un extra de una notificación específica.
 *
 * @param {Object} notification - La notificación a la que se accede.
 * @param {Object} extra - El extra a abrir.
 * @param {boolean} shared - Indica si la notificación es compartida.
 * @returns {Promise<any>} Una promesa que se resuelve con la respuesta del proceso de apertura.
 */
export async function openExtra(notification, extra, shared) {
  try {
    return (
      await NotificationsExtra({
        id: notification.id,
        extra,
        shared,
      })
    ).data
  } catch (error) {
    //console.log('Extra no disponible', extra.name)
  }
}

/**
 * Procesa una notificación específica, marcándola como en proceso.
 *
 * @param {string} notificationId - ID de la notificación a procesar.
 * @returns {Promise<any>} Una promesa que se resuelve con la respuesta del proceso.
 */
export async function notificationProcessing(notificationId) {
  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  const batch = Firestore.writeBatch(Database)

  batch.update(NotificationsDoc(userId, notificationId), {
    isProcessing: true,
    isCompleted: Firestore.deleteField(),
  })

  batch.set(
    HistoryDoc(
      userId,
      'notifications',
      notificationId,
      Firestore.doc(HistoryCollection(userId, 'notifications', notificationId))
        .id
    ),
    {
      actionType: 'processing',
      createdAt: Firestore.serverTimestamp(),
      // Contemplar modo sustitucion
      ...(store.getters.isSubstituteMode
        ? {
            userRef: UsersDoc(store.state.userSubstitute.id),
            absentUserRef: UsersDoc(store.state.user.id),
          }
        : {
            userRef: UsersDoc(store.state.user.id),
          }),
    },
    { merge: true }
  )

  return await batch.commit()
}

/**
 * Completa el procesamiento de una notificación específica.
 *
 * @param {string} notificationId - ID de la notificación a completar.
 * @returns {Promise<any>} Una promesa que se resuelve con la respuesta del proceso.
 */
export async function notificationCompleted(notificationId) {
  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  const batch = Firestore.writeBatch(Database)

  batch.update(NotificationsDoc(userId, notificationId), {
    isProcessing: Firestore.deleteField(),
    isCompleted: true,
  })

  batch.set(
    HistoryDoc(
      userId,
      'notifications',
      notificationId,
      Firestore.doc(HistoryCollection(userId, 'notifications', notificationId))
        .id
    ),
    {
      actionType: 'completed',
      createdAt: Firestore.serverTimestamp(),
      // Contemplar modo sustitucion
      ...(store.getters.isSubstituteMode
        ? {
            userRef: UsersDoc(store.state.userSubstitute.id),
            absentUserRef: UsersDoc(store.state.user.id),
          }
        : {
            userRef: UsersDoc(store.state.user.id),
          }),
    },
    { merge: true }
  )

  return await batch.commit()
}

/**
 * Revierte el estado de una notificación específica a su estado original.
 *
 * @param {string} notificationId - ID de la notificación a revertir.
 * @returns {Promise<any>} Una promesa que se resuelve con la respuesta del proceso.
 */
export async function notificationRevert(notificationId) {
  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  const batch = Firestore.writeBatch(Database)

  batch.update(NotificationsDoc(userId, notificationId), {
    isProcessing: Firestore.deleteField(),
    isCompleted: Firestore.deleteField(),
  })

  batch.set(
    HistoryDoc(
      userId,
      'notifications',
      notificationId,
      Firestore.doc(HistoryCollection(userId, 'notifications', notificationId))
        .id
    ),
    {
      actionType: 'reverted',
      createdAt: Firestore.serverTimestamp(),
      // Contemplar modo sustitucion
      ...(store.getters.isSubstituteMode
        ? {
            userRef: UsersDoc(store.state.userSubstitute.id),
            absentUserRef: UsersDoc(store.state.user.id),
          }
        : {
            userRef: UsersDoc(store.state.user.id),
          }),
    },
    { merge: true }
  )

  return await batch.commit()
}

/*export function isToday(dateA, dateB) {
  return dateA.toDateString() === dateB.toDateString() ? true : false
}*/

export function moreThanOneHour(date) {
  const HOUR = 1000 * 60 * 60
  const anHourAgo = Date.now() - HOUR

  return date < anHourAgo
}

export function getNotificationStatus(notification) {
  const currentDate = new Date()

  //modificamos el campo Estado en los customizados en front para que sea filtrable en la tabla

  if (notification.isCompleted) {
    notification.estado = 'Completada'
    return {
      color: 'completed',
      status: 'notificationStatus.completed',
    }
  } else if (notification.isProcessing) {
    notification.estado = 'En Trámite'
    return {
      color: 'processing',
      status: 'notificationStatus.processing',
    }
  } else if (notification.estado == 'Aceptado') {
    //notification.estado == 'Aceptado'
    return {
      color: 'accept',
      status: 'notificationStatus.accepted',
    }
  } else if (notification.estado == 'Rechazada') {
    //notification.estado == 'Aceptado'
    return {
      color: 'error',
      status: 'notificationStatus.rejected',
    }
  } else if (!notification.document && notification.isComparecida) {
    notification.estado = 'Comparecida'
    return {
      color: 'appeared',
      status: 'notificationStatus.appeared',
    }
  } else {
    // Si la fecha de hoy es posterior a la emision + 10 dias es que ha expirado
    if (
      notification.estado == 'Expirada' ||
      (notification.fecha_expiracion &&
        notification.fecha_expiracion < currentDate)
    ) {
      // si no está abierto ni comparecido calculamos el estado en funcion de la fecha de emision
      notification.estado = 'Expirada'
      return {
        color: 'error',
        status: 'notificationStatus.expired',
      }
    } else if (
      notification.fecha_expiracion &&
      currentDate >
        new Date(notification.fecha_expiracion).setDate(
          new Date(notification.fecha_expiracion).getDate() - 3
        )
    ) {
      notification.estado = 'Urgente'
      return {
        color: 'warning',
        status: 'notificationStatus.urgent',
      }
    } else {
      notification.estado = 'Pendiente'
      return {
        color: 'warningLow',
        status: 'notificationStatus.pending',
      }
    }
  }
}

/**
 * Given the i18n status return the status.
 * @param {String} status i18n status.
 * @returns Status string.
 */
export function getNotificationStatusFromI18n(status) {
  switch (status) {
    case 'notificationStatus.pending':
      return 'Pendiente'

    case 'notificationStatus.urgent':
      return 'Urgente'

    case 'notificationStatus.expired':
      return 'Expirada'

    case 'notificationStatus.appeared':
      return 'Comparecida'

    case 'notificationStatus.accepted':
      return 'Aceptado'

    case 'notificationStatus.processing':
      return 'En Trámite'

    case 'notificationStatus.completed':
      return 'Completada'

    default:
      return ''
  }
}

export function getNotificationType(notification) {
  return notification.tipo_envio == '1'
    ? {
        name: 'comunication',
        icon: 'mdi-email-fast',
      }
    : {
        name: 'notification',
        icon: 'mdi-email-lock', //'mdi-email-alert'
      }
}

export async function canOperatorBeManagerWithFilters(operator, notification) {
  const isSuperAdmin = !operator.parentRef

  if (isSuperAdmin) return true

  const activeCifs = getOperatorActiveCifs(operator)

  if (!activeCifs.has(notification.nif_titular)) {
    return false
  }

  // aqui ya entran los filtros
  const operatorRoles = await getCachedOperatorFilters(operator)

  return (
    filterNotifications([notification], { ...operator, operatorRoles }).length >
    0
  )
}

export async function getCachedOperatorFilters(operator) {
  if (!operatorFiltersCache.has(operator.id)) {
    operatorFiltersCache.set(operator.id, await getOperatorFilters(operator))
  }
  return operatorFiltersCache.get(operator.id)
}

export function buildCifOperatorsIndex(operators) {
  resetOperatorCaches()

  for (const operator of operators) {
    const activeCifs = getOperatorActiveCifs(operator)

    for (const cif of activeCifs) {
      if (!cifToOperatorsCache.has(cif)) {
        cifToOperatorsCache.set(cif, new Set())
      }
      cifToOperatorsCache.get(cif).add(operator)
    }
  }
}

function resetOperatorCaches() {
  heavyOperatorsMap.clear()
  operatorFiltersCache.clear()
  operatorActiveCifsCache.clear()
  cifToOperatorsCache.clear()
}

export function getOperatorActiveCifs(operator) {
  // Cachear CIFs activos por operador
  // Funcion de precalculo, una vez por operador

  const heavyOperator = operator.cifsPermissions
    ? operator
    : withHeavyCache(operator)

  const cached = operatorActiveCifsCache.get(heavyOperator.id)
  if (cached) return cached

  const activeCifs = new Set(
    Object.entries(heavyOperator.cifsPermissions ?? {})
      .filter(([, v]) => v.notificacionesActivas)
      .map(([cif]) => cif)
  )

  operatorActiveCifsCache.set(heavyOperator.id, activeCifs)
  return activeCifs
}

export function canOperatorBeManager(operator, notification) {
  const isSuperAdmin = !operator.parentRef

  if (isSuperAdmin) return true

  let cifs = Object.entries(operator.cifsPermissions)
    .filter(([, v]) => v.notificacionesActivas)
    .map(([k]) => k)

  return cifs.includes(notification.nif_titular)
}

export async function canGroupBeManager(group, notification) {
  if (group.deleted) return false

  return await groupCanSeeNotificationWithoutFilters(group, notification)
}

/**
 * Comprueba si un Grupo puede ver una notificación.
 * Condición: el CIF Titular de la notificación debe estar en la lista de empresas del Grupo.
 * No se toman en cuenta los Filtros del Grupo (GOODOK-2039)
 */
export async function groupCanSeeNotificationWithoutFilters(
  group,
  notification
) {
  const groupCifs = await Promise.all(
    group.cifsRef.map(async (cifRef) => await GetDocFromReference(cifRef))
  )

  let cifs = groupCifs
    .filter((cif) => cif.notificacionesActivas)
    .map((cif) => cif.numeroDocIdentidad)

  return cifs.includes(notification.nif_titular)
}

/**
 * Devuelve la lista con los números de Cifs de las empresas de un Grupo.
 */
export async function groupCifsList(group) {
  const groupCifs = await Promise.all(
    group.cifsRef.map(async (cifRef) => await GetDocFromReference(cifRef))
  )

  let cifs = groupCifs
    .filter((cif) => cif.notificacionesActivas)
    .map((cif) => cif.numeroDocIdentidad)

  return cifs
}

export function possibleManagers(operators, notification, user) {
  const normalizedUser = user.fullName
    ? user
    : {
        ...user,
        fullName: [user.name, user.surname]
          .filter((v) => typeof v === 'string' && v.trim() !== '')
          .join(' '),
      }

  const possibleManagers = [normalizedUser]

  for (let i = 0; i < operators.length; i++) {
    const operator = operators[i]
    if (
      !operator.disabled &&
      canOperatorBeManager(withHeavyCache(operator), notification)
    )
      possibleManagers.push(operator)
  }

  return possibleManagers
}

export async function possibleGroups(groups, notification) {
  // Del documento de requisitos:
  // "Un Grupo es asignable con el mismo criterio que los operadores: porque en su lista
  //  de operadores, al menos uno tiene en su lista de empresas a la titular de la notificación objeto."
  const possibleGroups = []
  for (let i = 0; i < groups.length; i++) {
    const group = groups[i]
    if (group.deleted) continue // el grupo ha sido borrado por marca. Ya no se puede operar con él.

    // Obtiene los operadores pertenecientes al grupo
    const operators = await getGroupOperators(group.usersRef)

    let foundAtLeastOnePossibleOperatorManager = false
    for (let j = 0; j < operators.length; j++) {
      const operator = operators[j]
      // Segun los requisitos, para un Grupo: sin filtros, solo las empresas.
      const isPossibleOperator = canOperatorBeManager(operator, notification)
      if (isPossibleOperator) {
        foundAtLeastOnePossibleOperatorManager = true
        break
      }
    }
    if (foundAtLeastOnePossibleOperatorManager) {
      possibleGroups.push(group)
    }
  }

  return possibleGroups
}

/**
 * Sube documentos añadidos a una notificación específica al sistema DMS.
 *
 * @param {string} notificationId - ID de la notificación.
 * @param {Object} data - Datos de los documentos a subir.
 */
export async function uploadAddedDocumentsToDMS(notificationId, data) {
  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  // Contemplar modo sustitucion
  // Actualizar la notificacion antes para insertar o borrar el atributo
  if (store.getters.isSubstituteMode) {
    await Firestore.updateDoc(NotificationsDoc(userId, notificationId), {
      absentUserId: store.state.user.id,
    })
  } else {
    await Firestore.updateDoc(NotificationsDoc(userId, notificationId), {
      absentUserId: Firestore.deleteField(),
    })
  }

  await NotificationsUploadExtraFiles({
    userId,
    notificationId,
    ...data,
  })
}

/**
 * Sube archivos de 'comparecencia' a una notificación específica.
 *
 * @param {string} notificationId - ID de la notificación.
 * @param {...} - Otros parámetros relevantes para la subida de archivos.
 */
export async function uploadComparecidaFiles(
  notificationId,
  mainDocs,
  acuseDocs,
  annexesDocs,
  fecha
) {
  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  await NotificationsUploadFiles({
    userId,
    notificationId,
    mainDocs,
    acuseDocs,
    annexesDocs,
    fecha,
  })

  await NotificationsOpen({ id: notificationId })
}

/**
 * Exporta notificaciones al sistema DMS.
 *
 * @param {Array<Object>} notifications - Notificaciones a exportar.
 * @returns {Promise<any>} Una promesa que se resuelve con la respuesta del proceso de exportación.
 */
export async function exportDmsNotifications(notifications) {
  return await ExportDmsNotifications({
    notifications,
  })
}

/**
 * Obtiene los usuarios que pueden ver una notificación específica.
 *
 * @param {Array<Object>} users - Lista de usuarios a considerar.
 * @param {Object} notification - La notificación a evaluar.
 * @returns {Promise<Array>} Una promesa que se resuelve con los usuarios que pueden ver la notificación.
 */
export async function usersThatCanViewNotification(users, notification) {
  // TODO esto es fake, hay que llamar a la funcion del servicio
  // console.log('notification=', notification)
  // const numOperators = Math.floor(Math.random() * 100 - 1)
  // for (let index = 0; index < numOperators; index++) {
  //   operators.push('Operador num. ' + index)
  // }

  let operators = []

  for (let i = 0; i < users.length; i++) {
    const operator = users[i]

    const canBeManager = await canOperatorBeManagerWithFilters(
      getHeavyOperator(operator), // optimizacion sobre mapa en cache
      notification
    )
    const possibleManager = {
      id: operator.id,
      fullName: operator?.fullName ?? `${operator?.name} ${operator?.surname}`,
      avatar: operator?.avatarURL,
      canBeManager: canBeManager,
      disabled: operator?.disabled,
    }
    operators.push(possibleManager)
  }

  operators = operators.filter((operator) => {
    return operator.canBeManager && !operator.disabled
  })

  if (
    notification.manager &&
    notification.manager.parentRef &&
    notification.manager.disabled === false &&
    (operators.length === 0 ||
      operators.find((oper) => oper.id === notification.manager.id) === false)
  ) {
    const manager = {
      id: notification.manager?.id,
      fullName: `${notification.manager?.name} ${notification.manager?.surname}`,
      avatar: notification.manager?.avatarURL,
    }
    operators.push(manager)
  }

  return operators
}

function getHeavyOperator(operator) {
  if (!heavyOperatorsMap.has(operator.id)) {
    heavyOperatorsMap.set(operator.id, withHeavyCache(operator))
  }
  return heavyOperatorsMap.get(operator.id)
}

/**
 * Obtiene los usuarios que pueden ver una notificación específica.
 *
 * @param {Array<Object>} users - Lista de usuarios a considerar.
 * @param {Object} notification - La notificación a evaluar.
 * @returns {Promise<Array>} Una promesa que se resuelve con los usuarios que pueden ver la notificación.
 */
export async function operatorsCanViewNotification(users, notifications) {
  const result = []

  for (let i = 0; i < notifications.length; i++) {
    const item = notifications[i]
    item.operators = await usersThatCanViewNotification(users, item)
    result.push(item)

    // Ceder el hilo cada X iteraciones para mostrar la barra de progreso fuida
    if (i % 200 === 0) {
      await new Promise(requestAnimationFrame)
    }
  }

  return result
}

/**
 * Actualiza el NIF Titular de una notificación en Firestore.
 *
 * @param {string} notificationId - ID de la notificación a actualizar.
 * @param {string} nif - Nif del titular a asignar a la notificación.
 * @returns {Promise<any>} - Promesa que se resuelve con la respuesta de la actualización.
 */
export async function updateNifTitular(notificationId, nif) {
  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  const batch = Firestore.writeBatch(Database)

  batch.update(NotificationsDoc(userId, notificationId), {
    nif_titular: nif,
  })

  batch.set(
    HistoryDoc(
      userId,
      'notifications',
      notificationId,
      Firestore.doc(HistoryCollection(userId, 'notifications', notificationId))
        .id
    ),
    {
      actionType: 'notificationNifTitularAdded',
      nif_titular: nif,
      createdAt: Firestore.serverTimestamp(),
      // Contemplar modo sustitucion
      ...(store.getters.isSubstituteMode
        ? {
            userRef: UsersDoc(store.state.userSubstitute.id),
            absentUserRef: UsersDoc(store.state.user.id),
          }
        : {
            userRef: UsersDoc(store.state.user.id),
          }),
    },
    { merge: true }
  )

  return await batch.commit()
}

/**
 * Construye y devuelve una lista de objetos con todos los estados de una notificacion, su color y su clave i18n.
 * Utilidad para poblar un selector
 *
 * @returns {Array<Object>} Un array de estados.
 */
export function buildStatusList() {
  return [
    {
      id: 0,
      estado: 'Pendiente',
      color: 'warningLow',
      status: 'notificationStatus.pending',
    },
    {
      id: 1,
      estado: 'Urgente',
      color: 'warning',
      status: 'notificationStatus.urgent',
    },
    {
      id: 2,
      estado: 'Aceptado',
      color: 'accept',
      status: 'notificationStatus.accepted',
    },
    {
      id: 3,
      estado: 'Expirada',
      color: 'error',
      status: 'notificationStatus.expired',
    },
    {
      id: 4,
      estado: 'En Trámite',
      color: 'processing',
      status: 'notificationStatus.processing',
    },
    {
      id: 5,
      estado: 'Completada',
      color: 'completed',
      status: 'notificationStatus.completed',
    },
    {
      id: 6,
      estado: 'Comparecida',
      color: 'appeared',
      status: 'notificationStatus.appeared',
    },
    {
      id: 7,
      estado: 'Rechazada',
      color: 'error',
      status: 'notificationStatus.rejected',
    },
  ]
}

/**
 * Devuelve cuantas notificaciones pertenecientes a una empresa tiene asignadas un operador (desde siempre, sin fechas)
 *
 * @param {string} userId - El ID del usuario.
 * @param {string} operatorId - El id del usuario operador.
 * @returns {Object} Un mapa cif -> contador , ejemplo { "B12345678": 2, "A99999999": 5 }
 */
export async function getNotificationCountsByCifForOperator(
  userId,
  operatorId
) {
  // Una unica lectura a Firestore: leer solo las notificaciones del operador, que haga el trabajo la bbdd.
  // Luego: agrupar por nif_titular y contar en memoria

  const docSnap = await Firestore.getDoc(UsersDoc(operatorId))
  if (!docSnap.exists() || !docSnap.data()) {
    return
  }
  const operatorRef = docSnap.ref

  const querySnap = await Firestore.getDocs(
    Firestore.query(
      NotificationsCollection(userId),
      Firestore.where('managerRef', '==', operatorRef)
    )
  )
  const countsByCif = Object.create(null)

  for (const doc of querySnap.docs) {
    const n = doc.data()
    if (!n.nif_titular) continue

    countsByCif[n.nif_titular] = (countsByCif[n.nif_titular] || 0) + 1
  }

  return countsByCif
}
